import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { WEDDING_STORIES, waLink, WA_MSG } from "../data/content";
import { Chapter, Reveal, Btn, useTitle, EASE } from "../components/shared";
import { ChevronLeft, ChevronRight, X, ArrowLeft } from "lucide-react";
import { WhatsAppIcon } from "../components/Navbar";

function PhotoCatalog({ photos, couple }) {
  const [lightbox, setLightbox] = useState(-1);
  const step = (dir) => setLightbox((p) => (p < 0 ? p : (p + dir + photos.length) % photos.length));
  return (
    <>
      <div className="columns-1 sm:columns-2 lg:columns-3 gap-5" data-testid="story-photo-grid">
        {photos.map((src, i) => (
          <motion.button
            key={`${src}-${i}`}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.1 }}
            transition={{ duration: 0.5, delay: (i % 6) * 0.04, ease: EASE }}
            onClick={() => setLightbox(i)}
            data-testid={`story-photo-${i}`}
            className="group relative mb-5 block w-full break-inside-avoid overflow-hidden text-left"
          >
            <img src={src} alt={`${couple} wedding photograph ${i + 1}`} loading="lazy" className="w-full object-cover transition-transform duration-[1.4s] ease-out group-hover:scale-105" />
            <div className="absolute inset-0 bg-emerald-deep/0 transition-colors duration-500 group-hover:bg-emerald-deep/20" />
          </motion.button>
        ))}
      </div>

      <AnimatePresence>
        {lightbox >= 0 && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-ink/95 p-4 md:p-12"
            data-testid="story-lightbox"
            onClick={() => setLightbox(-1)}
          >
            <button className="absolute right-5 top-5 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-ivory transition-colors hover:bg-gold hover:text-ink" data-testid="story-lightbox-close" onClick={() => setLightbox(-1)} aria-label="Close">
              <X className="h-5 w-5" />
            </button>
            <button className="absolute left-4 md:left-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-ivory transition-colors hover:bg-gold hover:text-ink" data-testid="story-lightbox-prev" onClick={(e) => { e.stopPropagation(); step(-1); }} aria-label="Previous">
              <ChevronLeft className="h-5 w-5" />
            </button>
            <motion.img
              key={lightbox}
              initial={{ opacity: 0, scale: 0.97 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.4, ease: EASE }}
              src={photos[lightbox]}
              alt={`${couple} photograph ${lightbox + 1}`}
              className="max-h-[84vh] max-w-full object-contain"
              onClick={(e) => e.stopPropagation()}
            />
            <button className="absolute right-4 md:right-8 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-gold/40 text-ivory transition-colors hover:bg-gold hover:text-ink" data-testid="story-lightbox-next" onClick={(e) => { e.stopPropagation(); step(1); }} aria-label="Next">
              <ChevronRight className="h-5 w-5" />
            </button>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}

export default function WeddingStory() {
  const { slug } = useParams();
  const idx = WEDDING_STORIES.findIndex((s) => s.slug === slug);
  const story = WEDDING_STORIES[idx];
  useTitle(story ? `${story.couple} — ${story.event} | The Big Wedd` : "Real Weddings | The Big Wedd");

  if (!story) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 bg-ivory px-6 text-center" data-testid="story-not-found">
        <p className="font-serif text-5xl text-emerald">This story is still being written</p>
        <p className="text-sm text-stone2">The celebration you're looking for is not here — explore our gallery instead.</p>
        <Btn to="/gallery" testid="story-not-found-cta">Explore The Gallery</Btn>
      </main>
    );
  }

  const next = WEDDING_STORIES[(idx + 1) % WEDDING_STORIES.length];

  return (
    <main>
      <section className="relative flex min-h-[72vh] items-end overflow-hidden bg-emerald-deep pb-14 pt-32 md:min-h-[80vh] md:pb-20" data-testid="story-hero">
        <motion.img
          src={story.cover}
          alt={`${story.couple} — ${story.event}`}
          initial={{ scale: 1.15 }}
          animate={{ scale: 1 }}
          transition={{ duration: 2.4, ease: EASE }}
          className="absolute inset-0 h-full w-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-emerald-deep via-emerald-deep/50 to-emerald-deep/20" />
        <div className="relative z-10 mx-auto w-full max-w-7xl px-6 md:px-12">
          <Link to="/gallery" data-testid="story-back-link" className="inline-flex items-center gap-3 text-[11px] uppercase tracking-[0.25em] text-ivory/70 transition-colors hover:text-gold-light">
            <ArrowLeft className="h-4 w-4" /> All Real Weddings
          </Link>
          <div className="mt-6">
            <Chapter num="—" title={`Real Wedding · ${story.style}`} dark />
          </div>
          <MaskReveal couple={story.couple} />
          <Reveal delay={0.4}>
            <p className="mt-5 flex flex-wrap items-center gap-x-4 gap-y-2 text-[11px] uppercase tracking-[0.22em] text-gold-light" data-testid="story-meta">
              <span>{story.event}</span><span className="text-gold">◆</span><span>{story.venue}</span>
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-ivory py-20 md:py-28" data-testid="story-text-section">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 md:grid-cols-12 md:px-12">
          <div className="md:col-span-4">
            <div className="md:sticky md:top-32">
              <Chapter num="01" title="The Story" />
              <Reveal delay={0.1}>
                <h2 className="mt-6 font-serif text-3xl md:text-4xl leading-tight text-emerald">
                  {story.couple}<br /><span className="italic text-gold-text">{story.venue}</span>
                </h2>
              </Reveal>
            </div>
          </div>
          <div className="md:col-span-8">
            <Reveal>
              <p className="font-serif text-2xl md:text-3xl leading-snug text-emerald">{story.story}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <p className="mt-6 text-sm md:text-base leading-relaxed text-stone2">
                Every function of this celebration was planned, designed and executed by The Big Wedd — decor, production, hospitality, artist management and on-ground coordination, all under one roof.
              </p>
            </Reveal>
            <Reveal delay={0.2}>
              <div className="mt-8 flex flex-col gap-4 sm:flex-row">
                <Btn to="/contact" testid="story-cta">Plan A Celebration Like This</Btn>
                <a href={waLink(WA_MSG)} target="_blank" rel="noopener noreferrer" data-testid="story-cta-whatsapp" className="inline-flex items-center gap-3 rounded-full border border-gold/60 px-8 py-4 text-[11px] md:text-xs font-medium uppercase tracking-[0.22em] text-emerald transition-all duration-500 hover:bg-gold hover:text-ink">
                  <WhatsAppIcon className="h-4 w-4" /> Chat on WhatsApp
                </a>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      <section className="bg-sand py-20 md:py-28" data-testid="story-catalog">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Chapter num="02" title="The Gallery" />
          <Reveal delay={0.1}>
            <h2 className="mt-6 font-serif text-4xl md:text-6xl text-emerald leading-[1.05]">
              {story.event} in <span className="italic text-gold-text">pictures</span>
            </h2>
          </Reveal>
          <div className="mt-12">
            <PhotoCatalog photos={story.photos} couple={story.couple} />
          </div>
        </div>
      </section>

      <section className="bg-ivory py-16 md:py-20" data-testid="story-next">
        <div className="mx-auto max-w-7xl px-6 md:px-12">
          <Reveal>
            <Link to={`/weddings/${next.slug}`} data-testid="story-next-link" className="group flex items-center justify-between gap-6 border border-gold/25 bg-white/70 p-6 md:p-10 transition-all duration-500 hover:border-gold/60">
              <div>
                <p className="text-[11px] uppercase tracking-[0.3em] text-gold-text">Next Celebration</p>
                <p className="mt-3 font-serif text-3xl md:text-5xl text-emerald">{next.couple}</p>
                <p className="mt-2 text-[11px] uppercase tracking-[0.22em] text-stone2">{next.event} · {next.venue}</p>
              </div>
              <span className="font-serif text-4xl text-gold transition-transform duration-500 group-hover:translate-x-3">→</span>
            </Link>
          </Reveal>
        </div>
      </section>
    </main>
  );
}

function MaskReveal({ couple }) {
  return (
    <span className="mt-6 block">
      <span className="block overflow-hidden">
        <motion.span
          className="block font-serif text-5xl md:text-7xl leading-[1.02] text-ivory"
          initial={{ y: "112%" }}
          animate={{ y: "0%" }}
          transition={{ duration: 1, delay: 0.25, ease: EASE }}
        >
          {couple}
        </motion.span>
      </span>
    </span>
  );
}
